/**
 * Team-limit notice — glass-floating pill shown when a sync push was
 * rejected with `message: "team_limit_reached"`. Without it a team created
 * offline beyond the license's team cap would silently vanish on the next
 * pull. The sync provider fires `TEAM_LIMIT_REACHED_EVENT` on `window`.
 */

import { AlertTriangle, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const TEAM_LIMIT_REACHED_EVENT = "sync:team-limit-reached";

const VISIBLE_MS = 10_000;

export function TeamLimitNotice() {
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<number | null>(null);

  useEffect(() => {
    const onReached = () => {
      setVisible(true);
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => setVisible(false), VISIBLE_MS);
    };
    window.addEventListener(TEAM_LIMIT_REACHED_EVENT, onReached);
    return () => {
      window.removeEventListener(TEAM_LIMIT_REACHED_EVENT, onReached);
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      className="glass-floating fixed bottom-4 left-1/2 z-270 flex max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-3 rounded-full border border-white/10 px-4 py-2.5 shadow-2xl"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
      }}
      role="status"
      aria-live="polite"
    >
      <AlertTriangle className="size-4 shrink-0 text-amber-300" aria-hidden />
      <span className="text-[13px] font-medium" style={{ color: "#f4f5f3" }}>
        Team-Limit erreicht – für weitere Teams bitte die Abrechnung prüfen oder den Support kontaktieren.
      </span>
      <button
        type="button"
        onClick={() => setVisible(false)}
        aria-label="Hinweis ausblenden"
        className="grid size-6 place-items-center rounded-full text-white/60 hover:bg-white/10"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
